import type { AssetMetricCatalogEntry } from '@src/providers/CoinMetricsApiProvider';
import { useEffect, useMemo, useState } from 'react';
import { useMissingMetrics, useSignalsAssetMap } from '../core';
import {
  ASSET_LEADERBOARD_1H_FREQUENCY,
  ASSET_LEADERBOARD_FREQUENCY,
  ASSET_LEADERBOARD_FRESHNESS_WINDOW_DAYS,
  ASSET_LEADERBOARD_METRICS,
  ASSET_LEADERBOARD_POLL_MS,
  ASSET_LEADERBOARD_PRICE_METRIC,
} from './tokens';

interface UseAssetLeaderboardUniverseResult {
  /** CoinMetrics asset id -> Talos `Currency.Symbol`, for every Talos-known, metric-complete asset. */
  talosSymbolByCmAssetId: ReadonlyMap<string, string>;
  /**
   * The subset of the resolved universe that additionally supports `ReferenceRateUSD` at `1h` — the
   * only assets the hourly 1H-change fetch (`useAssetLeaderboardOneHourChange`) may request, since
   * CoinMetrics rejects a whole batch if any requested asset lacks the metric at that frequency. Kept
   * separate from the main universe on purpose: an asset without 1h price still belongs on the board
   * (with 24H/7D and a blank 1H), so it must not be dropped from `talosSymbolByCmAssetId`.
   */
  cmAssetIdsWith1hPrice: readonly string[];
  isLoading: boolean;
  isError: boolean;
  /**
   * Required metrics that no catalog entry supports at all (see `detectMetricsMissingFromCatalog`) —
   * a strong signal the org's API key has no catalog access to that metric, for any asset. When this
   * is non-empty, `isAssetEligibleForAssetLeaderboard`'s all-required-metrics check can legitimately
   * exclude every single asset, resolving `talosSymbolByCmAssetId` to empty with no error of its own —
   * this is what lets the caller tell that apart from a genuinely empty universe.
   */
  missingMetrics: string[];
}

const EMPTY_ID_LIST: readonly string[] = [];
const EMPTY_MISSING_METRICS: string[] = [];

/** `Date.now()` rounded down to the poll interval — see the note on the `now` state below. */
function currentFreshnessClockBucket(): number {
  return Math.floor(Date.now() / ASSET_LEADERBOARD_POLL_MS) * ASSET_LEADERBOARD_POLL_MS;
}

/**
 * True when `entry` supports every metric in `requiredMetrics` at `frequency`, AND each of those
 * metrics' catalog `max_time` is no older than `freshnessWindowDays` relative to `now`.
 *
 * The metric+frequency check alone isn't enough: CoinMetrics rejects a `/timeseries/asset-metrics`
 * request outright (`bad_parameter: "All requested metrics aren't supported for asset '<id>' and
 * frequency '<freq>'"`) if ANY requested asset is missing ANY requested metric at that frequency, so
 * eligibility must require ALL metrics, not just at least one.
 *
 * The freshness check catches a different problem the metric+frequency check can't: an asset that
 * stopped trading/reporting long ago can still have a historical catalog entry for every required
 * metric+frequency, pass that check, and then have CoinMetrics return its last-ever (but very old)
 * points anyway (we fetch with `paging_from: 'end'`, which returns whatever the newest available data
 * is, however old) — rendering stale numbers that look exactly like current ones. Checking `max_time`
 * up front excludes it before it's ever fetched. Exported for unit testing.
 */
export function isAssetEligibleForAssetLeaderboard(
  entry: AssetMetricCatalogEntry,
  requiredMetrics: readonly string[],
  frequency: string,
  now: Date,
  freshnessWindowDays: number
): boolean {
  const freshnessCutoffMs = now.getTime() - freshnessWindowDays * 24 * 60 * 60 * 1000;

  return requiredMetrics.every(requiredMetric =>
    entry.metrics.some(metricEntry => {
      if (metricEntry.metric !== requiredMetric) {
        return false;
      }
      const frequencyEntry = metricEntry.frequencies.find(f => f.frequency === frequency);
      if (!frequencyEntry) {
        return false;
      }
      const maxTimeMs = new Date(frequencyEntry.max_time).getTime();
      return Number.isFinite(maxTimeMs) && maxTimeMs >= freshnessCutoffMs;
    })
  );
}

/**
 * Resolves the Talos-known CoinMetrics asset universe from `/reference-data/assets`, intersected with
 * the org's known currencies AND with `/catalog-v2/asset-metrics` eligibility — only assets that
 * support all of `ASSET_LEADERBOARD_METRICS` at `ASSET_LEADERBOARD_FREQUENCY`, with data no older than
 * `ASSET_LEADERBOARD_FRESHNESS_WINDOW_DAYS`, are included. Per product ("pull in all the assets for which we
 * have all the datasets we are looking to display"), and required for correctness: without the
 * metric+frequency check, a single ineligible asset in a batch fails the whole metrics request; without
 * the freshness check, a delisted/inactive asset can still pass and render stale data as if current
 * (see `isAssetEligibleForAssetLeaderboard`'s doc for both).
 *
 * Reference-data resolution itself is delegated to `useSignalsAssetMap` (shared with every other Intel
 * module) via the `isAssetEligible` option, which applies the metric+frequency+freshness check *before*
 * that hook's own Talos-symbol dedup — preserving "filter, then dedupe" ordering exactly, since filtering
 * after dedup could let a duplicate-symbol entry survive dedup only to be dropped by eligibility,
 * silently losing an asset that a different (eligible) duplicate entry would have kept. Only the catalog
 * query is polled: `max_time` (used by the freshness check) lives solely in the catalog response, so only
 * it needs to stay current in a long-lived session — the reference-data response has no time-sensitive
 * fields and is intentionally left un-polled to avoid forcing the other Intel modules that share its exact
 * RTK Query cache entry (same `{include:'talos', page_size:10000}` args) into polling too. The tradeoff:
 * in a long-lived session, the Talos-symbol mapping itself is effectively frozen at first fetch — an asset
 * gaining or losing a Talos mapping mid-session won't be picked up until that cache entry's
 * `keepUnusedDataFor` (24h) lapses or the app reloads, even though its catalog eligibility keeps polling.
 */
export function useAssetLeaderboardUniverse(): UseAssetLeaderboardUniverseResult {
  // Rounded to the poll interval (rather than "now at mount") so every panel — regardless of when it
  // happened to mount — computes the same freshness cutoff at any given moment. Two panels resolving the
  // same universe from unaligned per-panel clocks could otherwise disagree, right at the freshness
  // boundary, on whether a given asset is eligible — producing two different cache-key signatures and
  // silently defeating the snapshot-sharing this whole hook chain exists to enable.
  //
  // The state update must land at that same boundary too, or the quantized value above is undermined: a
  // plain `setInterval(..., ASSET_LEADERBOARD_POLL_MS)` fires on a schedule anchored to mount time, so a
  // panel mounted mid-bucket won't pick up the new bucket until up to a full poll interval after the
  // boundary actually passed — during which it can disagree with a panel that mounted closer to the
  // boundary. Scheduling each tick's delay from the boundary itself (rather than from the previous tick)
  // keeps every panel's state transition aligned to the same instant, bounded only by normal timer jitter.
  const [now, setNow] = useState(currentFreshnessClockBucket);
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const scheduleNextTick = () => {
      const delayMs = ASSET_LEADERBOARD_POLL_MS - (Date.now() % ASSET_LEADERBOARD_POLL_MS);
      timeoutId = setTimeout(() => {
        setNow(currentFreshnessClockBucket());
        scheduleNextTick();
      }, delayMs);
    };

    scheduleNextTick();
    return () => clearTimeout(timeoutId);
  }, []);

  // Stable across a given `now` tick (changes hourly, not every render) so it doesn't invalidate
  // useSignalsAssetMap's internal memoization on every render.
  const isAssetEligible = useMemo(() => {
    const nowDate = new Date(now);
    return (entry: AssetMetricCatalogEntry) =>
      isAssetEligibleForAssetLeaderboard(
        entry,
        ASSET_LEADERBOARD_METRICS,
        ASSET_LEADERBOARD_FREQUENCY,
        nowDate,
        ASSET_LEADERBOARD_FRESHNESS_WINDOW_DAYS
      );
  }, [now]);

  const { talosSymbolByCmAssetId, catalogEntries, isLoading, isError } = useSignalsAssetMap(ASSET_LEADERBOARD_METRICS, {
    pollingInterval: ASSET_LEADERBOARD_POLL_MS,
    isAssetEligible,
  });

  // A second, independent eligibility pass over the same cached catalog response (see the field doc on
  // `cmAssetIdsWith1hPrice` above for why it must stay separate from `talosSymbolByCmAssetId`).
  const cmAssetIdsWith1hPrice = useMemo(() => {
    if (catalogEntries.length === 0 || talosSymbolByCmAssetId.size === 0) {
      return EMPTY_ID_LIST;
    }

    const nowDate = new Date(now);
    const oneHourEligibleCmAssetIds = new Set(
      catalogEntries
        .filter(entry =>
          isAssetEligibleForAssetLeaderboard(
            entry,
            [ASSET_LEADERBOARD_PRICE_METRIC],
            ASSET_LEADERBOARD_1H_FREQUENCY,
            nowDate,
            ASSET_LEADERBOARD_FRESHNESS_WINDOW_DAYS
          )
        )
        .map(entry => entry.asset)
    );

    // Preserves talosSymbolByCmAssetId's insertion order (== the /reference-data/assets response order).
    return Array.from(talosSymbolByCmAssetId.keys()).filter(cmAssetId => oneHourEligibleCmAssetIds.has(cmAssetId));
  }, [catalogEntries, talosSymbolByCmAssetId, now]);

  const catalogMissingMetrics = useMissingMetrics(
    catalogEntries,
    ASSET_LEADERBOARD_METRICS,
    isLoading,
    ASSET_LEADERBOARD_FREQUENCY
  );
  // A failed catalog request also leaves catalogEntries empty, which looks identical to a genuine
  // entitlement gap to useMissingMetrics — only trust it once the catalog actually loaded.
  const missingMetrics = isError ? EMPTY_MISSING_METRICS : catalogMissingMetrics;

  return { talosSymbolByCmAssetId, cmAssetIdsWith1hPrice, isLoading, isError, missingMetrics };
}
